import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-[#cc0404] text-white hover:bg-[#a30303] rounded-[4px]',
        destructive: 'bg-red-600 text-white hover:bg-red-700 rounded-[4px]',
        outline: 'border border-gray-300 bg-white hover:bg-gray-50 rounded-[4px]',
        secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 rounded-[4px]',
        ghost: 'hover:bg-gray-100',
        link: 'text-[#cc0404] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-[15px]',
        sm: 'h-9 rounded-[4px] px-3',
        lg: 'h-11 rounded-[4px] px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
